import { Component, computed, output, signal } from '@angular/core'

import { FormsModule } from '@angular/forms'
import { EntityRef } from 'src/app/shared/models/referenceData'
import { DateRangeFilterStateValue } from 'src/app/shared/services/filter/date-range-filter'
import { DateRangeFilterPickerComponent } from '../date-range-filter-picker/date-range-filter-picker.component'
import { EntitySelectorComponent } from '../entity-selector/entity-selector.component'
import { LabeledPropertyComponent } from '../labeled-property/labeled-property.component'
import { TBillCACPHourOverride, TBillCACPHourOverrideRow } from '../settlements/tbill-settlements.types'
import { DateDisplayComponent } from '../date-display/date-display.component'
import { monthOnlyFormat } from '../../shared/utilities/date'
import { hourOverrideHeaders } from './tbill-ca-cp-hour-overrides.component'
import { ModalFooterComponent } from '../modals/modal-footer/modal-footer.component'
import { ModalHeaderComponent } from '../modals/modal-header/modal-header.component'

type InternalState = Readonly<{
  month?: DateRangeFilterStateValue
  hour?: DateRangeFilterStateValue
  control_area?: EntityRef
}>

type InitArgs = Readonly<{
  row?: TBillCACPHourOverrideRow
  onSave: (override: TBillCACPHourOverride) => void
  onCancel: () => void
}>

@Component({
  imports: [
    FormsModule,
    LabeledPropertyComponent,
    DateRangeFilterPickerComponent,
    EntitySelectorComponent,
    DateDisplayComponent,
    ModalFooterComponent,
    ModalHeaderComponent,
  ],
  selector: 'app-tbill-ca-cp-hour-override-modal',
  template: `
    <div app-modal-header>{{ action() }} Override</div>
    <div class="px-4 py-2">
      <div app-labeled-property [label]="headers.control_area_code">
        @if (row()) {
          {{ row()!.control_area_code }}
        } @else {
          <div
              app-entity-selector
              [slim]="true"
              [label]="headers.control_area_code"
              [config]="{ resource: 'controlAreas', allowMultiple: false }"
              [value]="internalState().control_area"
              (valueChange)="updateControlArea($event)"
          >
          </div>
        }
      </div>
      <div app-labeled-property [label]="headers.month">
        @if (row()) {
          <div app-date-display [value]="row()!.month" [format]="monthOnlyFormat"></div>
        } @else {
          <div dateRangeFilterPicker
              [value]="internalState().month"
              (valueChange)="updateMonth($event)"
              [showOperation]="false"
              [showRange]="false"
              [showTime]="false"
              [showSeconds]="false"
              [onlyMonth]="true"
          >
          </div>
        }
      </div>
      <div app-labeled-property [label]="headers.hour">
        <div dateRangeFilterPicker
            [value]="internalState().hour"
            (valueChange)="updateHour($event)"
            [showOperation]="false"
            [showRange]="false"
            [showTime]="true"
            [showSeconds]="false"
            [onlyMonth]="false"
        >
        </div>
      </div>
    </div>
    <div app-modal-footer>
      <button
          class="action-btn primary"
          [disabled]="!overrideDef()"
          (click)="onSave.emit(overrideDef()!)"
      >
        {{ action() }}
      </button>
      <button class="action-btn" (click)="onCancel.emit()">Cancel</button>
    </div>
  `,
  styles: `
    div[app-modal-footer] {
      padding-right: 1.5rem;
      margin-bottom: 1rem;
    }
  `,
})
export class TBillCACPHourOverrideModalComponent {
  protected readonly row = signal<TBillCACPHourOverrideRow | undefined>(undefined)
  protected readonly internalState = signal<InternalState>({})

  protected action = computed((): 'Create' | 'Update' => this.row() ? 'Update' : 'Create')

  protected readonly overrideDef = computed((): TBillCACPHourOverride | undefined => {
    const row = this.row()
    const hour = this.internalState().hour?.value as Date | undefined
    if (!hour) {
      return undefined
    } else if (row) {
      return row.hour.valueOf() === hour.valueOf()
        ? undefined
        : { control_area_code: row.control_area_code, month: row.month, hour }
    } else {
      const { control_area, month } = this.internalState()
      return control_area && month
        ? { control_area_code: control_area.entity_code, month: month.value as Date, hour }
        : undefined
    }
  })

  protected readonly onCancel = output()
  protected readonly onSave = output<TBillCACPHourOverride>()

  protected readonly headers = hourOverrideHeaders

  protected readonly monthOnlyFormat = monthOnlyFormat

  init({ row, onCancel, onSave }: InitArgs) {
    if (row) {
      this.row.set(row)
      this.internalState.set({ ...this.internalState(), hour: { operation: 'eq', value: row.hour } })
    }
    this.onSave.subscribe(onSave)
    this.onCancel.subscribe(onCancel)
  }

  protected updateMonth(month: DateRangeFilterStateValue) {
    this.internalState.set({ ...this.internalState(), month })
  }

  protected updateControlArea(control_area: EntityRef) {
    this.internalState.set({ ...this.internalState(), control_area })
  }

  protected updateHour(hour: DateRangeFilterStateValue) {
    this.internalState.set({ ...this.internalState(), hour })
  }
}
